import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { resolveUploadUrl } from '../../utils/apiClient';
import styles from './Sidebar.module.css';
import SidebarItem from './SidebarItem.js';
import { usePermissions } from '../../hooks/usePermission';
import { usePendingApprovals } from '../../context/PendingApprovalsContext';
import ConfirmDialog from '../ConfirmDialog';

import DirectoryIcon from '../../assets/directory.png';
import LeadsIcon from '../../assets/lead.png';
import ProjectsIcon from '../../assets/project.png';
import PeopleIcon from '../../assets/people.png';
import ScheduleIcon from '../../assets/schedule.png';
import TimesheetsIcon from '../../assets/timesheet.png';
import HomeIcon from '../../assets/home.png';
import SettingsIcon from '../../assets/setting.png';
import LogoutIcon from '../../assets/logout.png';
import LeaveIcon from '../../assets/leave.png';

export default function Sidebar({ collapsed, onToggle, user, onLogout }) {
  const navigate = useNavigate();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showProfileMenu && !e.target.closest(`.${styles.profileContainer}`)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showProfileMenu]);

  const {
    canViewLeads,
    canAccessProjects,
    canViewPeople,
    canAccessSchedule,
    canAccessTimesheets,
    canAccessDirectory,
    canAccessTimeOff,
  } = usePermissions();
  const { pendingCount } = usePendingApprovals();

  // Define sidebar items with permission requirements
  const sidebarItems = [
    { 
      label: "Dashboard", 
      icon: HomeIcon, 
      path: "", 
      show: true,
      end: true // Only highlight when exactly on /dashboard/
    },
    { 
      label: "Leads", 
      icon: LeadsIcon, 
      path: "leads",
      show: canViewLeads
    },
    { 
      label: "Projects", 
      icon: ProjectsIcon, 
      path: "projects",
      show: canAccessProjects
    },
    { 
      label: "Schedule", 
      icon: ScheduleIcon, 
      path: "schedule",
      show: canAccessSchedule // view_schedule OR view_own_schedule
    },
    { 
      label: "Timesheets", 
      icon: TimesheetsIcon, 
      path: "timesheets",
      show: canAccessTimesheets // view_timesheets OR view_own_timesheets
    },
    {
      label: "Leave Manager",
      icon: LeaveIcon,
      path: "leave-manager",
      show: canAccessTimeOff,
      badge: pendingCount
    },
    { 
      label: "People & Resources",
      icon: PeopleIcon,
      path: "people",
      show: canViewPeople
    },
    { 
      label: "Directory", 
      icon: DirectoryIcon, 
      path: "directory",
      show: canAccessDirectory // view_internal_directory OR view_external_directory
    },
  ];

  // Filter items based on permissions
  const visibleItems = sidebarItems.filter(item => item.show);

  // Get user initials for avatar
  const userInitials = user?.name 
    ? user.name.trim().split(/\s+/).slice(0, 2).map(p => p[0]?.toUpperCase() || "").join("") || "U"
    : "U";

  return (
    <div className={`${styles.sidebar} ${collapsed ? styles.collapsed : ''}`}>
      <div className={styles.toggle} onClick={onToggle}>
        {collapsed ? '▶' : '◀'}
      </div>

      {/* Main navigation items */}
      <div className={styles.navItems}>
        {visibleItems.map((item, i) => (
          <SidebarItem
            key={i}
            label={item.label}
            icon={item.icon}
            path={item.path}
            collapsed={collapsed}
            end={item.end}
            badge={item.badge}
          />
        ))}
      </div>

      {/* Bottom section with profile menu */}
      <div className={styles.bottomSection}>
        {/* User profile with dropdown */}
        <div className={styles.profileContainer}>
          <div 
            className={styles.profileAvatar}
            onClick={() => setShowProfileMenu(!showProfileMenu)}
            title={user?.name || 'Profile'}
          >
            {user?.avatar_url ? (
              <img src={resolveUploadUrl(user.avatar_url)} alt="Profile" className={styles.avatarImg} />
            ) : (
              <span className={styles.avatarFallback}>{userInitials}</span>
            )}
            {!collapsed && <span className={styles.label}>{user?.name || 'Profile'}</span>}
          </div>

          {/* Profile dropdown menu */}
          {showProfileMenu && (
            <div className={styles.profileMenu}>
              <div 
                className={styles.menuItem}
                onClick={() => {
                  navigate('/dashboard/settings');
                  setShowProfileMenu(false);
                }}
              >
                <img src={SettingsIcon} alt="Settings" className={styles.menuIcon} />
                <span>Settings</span>
              </div>
              <div
                className={`${styles.menuItem} ${styles.menuItemDisabled}`}
                title="Coming soon"
              >
                <svg className={styles.menuIconSvg} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="2" y="5" width="20" height="14" rx="2" />
                  <line x1="2" y1="10" x2="22" y2="10" />
                </svg>
                <span>Billing</span>
              </div>
              <div
                className={`${styles.menuItem} ${styles.menuItemDisabled}`}
                title="Coming soon"
              >
                <svg className={styles.menuIconSvg} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                </svg>
                <span>Product Updates</span>
              </div>
              <div
                className={`${styles.menuItem} ${styles.menuItemDisabled}`}
                title="Coming soon"
              >
                <svg className={styles.menuIconSvg} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="8" y1="13" x2="16" y2="13" />
                  <line x1="8" y1="17" x2="16" y2="17" />
                </svg>
                <span>Terms &amp; Policies</span>
              </div>
            </div>
          )}
        </div>

        {/* Logout */}
        <div 
          className={styles.bottomItem}
          onClick={() => setShowLogoutConfirm(true)}
          title="Logout"
        >
          <img src={LogoutIcon} alt="Logout" className={`${styles.icon} ${styles.logoutIcon}`} />
          {!collapsed && <span className={styles.label}>Logout</span>}
        </div>
      </div>

      {/* Logout Confirmation Dialog */}
      {showLogoutConfirm && (
        <ConfirmDialog
          message="Are you sure you want to logout?"
          confirmText="Logout"
          cancelText="Cancel"
          onConfirm={() => {
            setShowLogoutConfirm(false);
            onLogout();
          }}
          onCancel={() => setShowLogoutConfirm(false)}
        />
      )}
    </div>
  );
}
